import React from 'react';

import { Skeleton } from '../Skeleton/Skeleton';
import { Wrap, Root, HeadCell, HeadLabel, Row, Cell, type TableAlign } from './Table.styles';

export type { TableAlign };

export type TableColumn<T> = {
  /** Stable column id (React key; also the align/width hook). */
  id: string;
  /**
   * Header copy. Translate at the call site (`header: t('Title')`) so the
   * extractor harvests the literal; an empty string renders a blank header
   * (e.g. a trailing actions column).
   */
  header: string;
  /** Cell renderer for a data row. */
  cell: (row: T) => React.ReactNode;
  align?: TableAlign;
  /** Fixed CSS width for the column (e.g. `'120px'`); omitted, the column sizes itself. */
  width?: string;
};

export type TableProps<T> = {
  columns: ReadonlyArray<TableColumn<T>>;
  rows: ReadonlyArray<T>;
  /** Stable row key (never the array index; rows re-sort under filters). */
  rowKey: (row: T) => string;
  /** Accessible table name. Translate at the call site (`label={t('Blog articles')}`). */
  label: string;
  /** Render placeholder rows instead of data while the source loads. */
  loading?: boolean;
  /** How many placeholder rows the loading state shows. */
  skeletonRows?: number;
  onRowClick?: (row: T) => void;
  className?: string;
};

/**
 * Semantic data table on the area's panel surfaces: uppercase muted headers,
 * hairline row separators, no outer chrome (wrap it in a `Panel`). Columns are
 * declarative `{ header, cell }` renderers, so status badges / action rows are
 * plain React nodes. On narrow viewports the table keeps its min width and pans
 * horizontally inside its own scroll container. While `loading`, it renders
 * `skeletonRows` placeholder rows so the header + layout are stable before data.
 */
export const Table = <T,>({
  columns,
  rows,
  rowKey,
  label,
  loading = false,
  skeletonRows = 4,
  onRowClick,
  className,
}: TableProps<T>): React.ReactElement => (
  <Wrap className={className}>
    <Root aria-label={label} aria-busy={loading || undefined}>
      <thead>
        <tr>
          {columns.map((col) => (
            <HeadCell key={col.id} scope="col" $align={col.align ?? 'left'} $width={col.width}>
              {col.header ? <HeadLabel text={col.header} tag="span" /> : null}
            </HeadCell>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading
          ? Array.from({ length: skeletonRows }, (_, i) => (
              // Placeholder rows are static and decorative; the index key is safe here.
              <Row key={`skeleton-${i}`} $clickable={false} aria-hidden="true">
                {columns.map((col) => (
                  <Cell key={col.id} $align={col.align ?? 'left'}>
                    <Skeleton width="60%" height={14} />
                  </Cell>
                ))}
              </Row>
            ))
          : rows.map((row) => (
              <Row
                key={rowKey(row)}
                $clickable={Boolean(onRowClick)}
                // Clickable rows are keyboard-reachable too: focusable, and
                // Enter/Space activate like a click.
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (event: React.KeyboardEvent<HTMLTableRowElement>) => {
                        if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((col) => (
                  <Cell key={col.id} $align={col.align ?? 'left'}>
                    {col.cell(row)}
                  </Cell>
                ))}
              </Row>
            ))}
      </tbody>
    </Root>
  </Wrap>
);

export default Table;
